import { useState, useEffect, useRef } from 'react';
import { authApi } from '../services/api';
import { useApp } from '../AppContext';
import cFirstLogo from '../images/SwiftCheqLogo.png';

export function AuthNavbar({ onNavigate, showLogin = true, showRegister = false }) {
  return (
    <nav className="topnav" style={{ justifyContent: 'space-between' }}>
      <div className="logo" onClick={() => onNavigate('login')}>
        <img src={cFirstLogo} alt="cFIRST BGV" style={{ height: '36px', width: 'auto' }} />
      </div>
      <div className="flex g8 aic">
        {showLogin && <><span className="ts tg">Already registered?</span><button className="btn bpri bsm" onClick={() => onNavigate('login')}>Login →</button></>}
        {showRegister && <><span className="ts tg">New to SwiftCheq?</span><button className="btn bout bsm" onClick={() => onNavigate('register-company')}>Register</button></>}
      </div>
    </nav>
  );
}

// Same progress bar style already used on the Register page, generalized with a
// `current` step index so other wizard pages (e.g. DigiSign) can reuse it.
export function WizardSteps({ steps, current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0', marginBottom: '28px' }}>
      {steps.map((s, i) => (
        <div key={s} style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: i === current ? 'var(--or)' : 'var(--g200)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, color: i === current ? 'var(--nv)' : 'var(--g400)' }}>{i + 1}</div>
            <div style={{ fontSize: '10px', color: i === current ? 'var(--or-dk)' : 'var(--g400)', fontWeight: i === current ? 700 : 400, whiteSpace: 'nowrap' }}>{s}</div>
          </div>
          {i < steps.length - 1 && <div style={{ width: '40px', height: '2px', background: 'var(--g200)', margin: '0 4px', marginBottom: '16px' }} />}
        </div>
      ))}
    </div>
  );
}

// ── Login Page ────────────────────────────────────────────
export function Login({ onNavigate }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpMode, setOtpMode] = useState(false);
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const { login, addToast } = useApp();

  async function handleLogin() {
    const missingEmail = !email.trim();
    const missingPassword = !password.trim();
    setEmailError(missingEmail ? 'Username is required' : '');
    setPasswordError(missingPassword ? 'Password is required' : '');
    if (missingEmail || missingPassword) return;

    setLoading(true);
    try {
      const res = await authApi.login(email, password);
      if (res.redirectto === 'ChangePasswordForcefully') {
        if (res.token) localStorage.setItem('token', res.token);
        onNavigate('change-password', { username: res.username });
        return;
      }
      if (res.page === 'ClientAuth') {
        if (res.token) localStorage.setItem('token', res.token);
        onNavigate('client-auth', {
          clientotp: res.clientOTP || res.clientotp,
          otpexpire: res.OTPExpire || res.otpexpire,
          authToken: res.token,
          authUser:  res.user || null,
        });
        return;
      }
      if (res.page === 'ClientOnBoarding') {
        if (res.token) localStorage.setItem('token', res.token);
        if (res.user) login(res.user, res.token, res.clientid);
        onNavigate('client-onboarding', {
          liclass:  res.liclass  || 1,
          clientId: res.clientid || '',
          status:   res.status   || '',
          allDone:  res.status === 'PENDING FROM FINANCE',
        });
        return;
      }
      if (res.page === 'CompleteRegistration') {
        // First login for a self-signed-up account — send them into the
        // Company Info / GST Details / Agreement wizard to finish registration.
        if (res.token) localStorage.setItem('token', res.token);
        if (res.user) login(res.user, res.token, res.clientId);
        onNavigate('register', {
          prefillCompanyName: res.user?.ClientName      || '',
          prefillContactName: res.user?.UserDescription || '',
          prefillEmail:       res.user?.EmailId         || '',
        });
        return;
      }
      if (!res.token) {
        addToast(res.message || 'Login failed', 'error');
        return;
      }
      login(res.user, res.token, res.clientid);
      const role = (res.user?.role || res.user?.Role || res.role || '').toLowerCase();
      onNavigate(role === 'superadmin' ? 'admin-dashboard' : 'dashboard');
    } catch (err) {
      addToast(err.message || 'Invalid username or password', 'error');
    } finally { setLoading(false); }
  }

  return (
    <>
      <AuthNavbar onNavigate={onNavigate} showLogin={false} showRegister />
      <div className="auth-wrap">
        <div className="auth-box">
          <div className="auth-logo">
               <img src={cFirstLogo} alt="cFIRST BGV" style={{ height: '36px', width: 'auto' }} />
            <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--nv)' }}>Welcome back</div>
            <div style={{ fontSize: '13px', color: 'var(--g600)', marginTop: '3px' }}>Sign in to your SwiftCheq account</div>
          </div>
        
          <div className="card" style={{ boxShadow: '0 8px 40px rgba(26,31,54,.13)' }}>
            {!otpMode ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div className="fg">
                  <label className="flbl">UserName <span className="req">*</span></label>
                  <input
                    className="inp" value={email}
                    onChange={e => { setEmail(e.target.value.toUpperCase()); if (emailError) setEmailError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter' && !loading) handleLogin(); }}
                    style={{ textTransform: 'uppercase', ...(emailError ? { borderColor: 'var(--rd)' } : {}) }}
                  />
                  {emailError && <div style={{ fontSize: '11.5px', color: 'var(--rd)', marginTop: 2 }}>{emailError}</div>}
                </div>
                <div className="fg">
                  <label className="flbl">Password <span className="req">*</span></label>
                  <input
                    className="inp" type="password" value={password}
                    onChange={e => { setPassword(e.target.value); if (passwordError) setPasswordError(''); }}
                    onKeyDown={e => { if (e.key === 'Enter' && !loading) handleLogin(); }}
                    style={passwordError ? { borderColor: 'var(--rd)' } : {}}
                  />
                  {passwordError && <div style={{ fontSize: '11.5px', color: 'var(--rd)', marginTop: 2 }}>{passwordError}</div>}
                  <div style={{ textAlign: 'right', marginTop: '4px' }}><span style={{ fontSize: '11.5px', color: 'var(--or-dk)', fontWeight: 600, cursor: 'pointer' }} onClick={() => onNavigate('forgot-password')}>Forgot password?</span></div>
                </div>
                {/* <div style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '10px 12px', background: 'var(--g50)', borderRadius: 'var(--rs)', border: '1px solid var(--g200)' }}>
                  <input type="checkbox" style={{ accentColor: 'var(--or)' }} defaultChecked />
                  <span style={{ fontSize: '12px', color: 'var(--g600)' }}>Remember me on this device</span>
                </div> */}
                <button className="btn bpri wf blg" onClick={handleLogin} disabled={loading} style={loading ? { cursor: 'not-allowed', opacity: 0.92 } : {}}>
                  {loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                      <span style={{ width: 18, height: 18, border: '2.5px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin .7s linear infinite', display: 'inline-block', flexShrink: 0 }} />
                      Signing in…
                    </span>
                  ) : 'Login →'}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div className="fg"><label className="flbl">Mobile Number <span className="req">*</span></label><input className="inp" placeholder="+91 XXXXX XXXXX" value={mobile} onChange={e => setMobile(e.target.value)} /></div>
                <div className="fg"><label className="flbl">OTP</label><input className="inp" placeholder="Enter 6-digit OTP" value={otp} onChange={e => setOtp(e.target.value)} style={{ letterSpacing: '6px', fontSize: '18px', fontWeight: 700, textAlign: 'center' }} /></div>
                <button className="btn bpri wf blg" onClick={handleLogin}>Verify OTP →</button>
              </div>
            )}
            {/*<div style={{ position: 'relative', margin: '18px 0' }}>
              <div style={{ borderTop: '1px solid var(--g200)' }} />
              <div style={{ position: 'absolute', top: '-9px', left: '50%', transform: 'translateX(-50%)', background: 'var(--wh)', padding: '0 12px', fontSize: '11px', color: 'var(--g400)' }}>OR</div>
            </div>
             <button className="btn bout wf" style={{ justifyContent: 'center' }} onClick={() => setOtpMode(!otpMode)}>
              {otpMode ? '📧 Login with Email' : '📱 Login with OTP (Mobile)'}
            </button> */}
            <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '12.5px', color: 'var(--g600)' }}>
              New to SwiftCheq? <span style={{ color: 'var(--or-dk)', fontWeight: 600, cursor: 'pointer' }} onClick={() => onNavigate('register-company')}>Register your company</span>
            </div>
          </div>
            <div role="alert" style={{ background: 'var(--am-lt)', border: '1px solid var(--am)', borderLeft: '4px solid var(--am)', borderRadius: 'var(--rs)', padding: '12px 14px', marginTop: '16px', fontSize: '12.5px', color: 'var(--g800)', lineHeight: 1.55 }}>
            <div style={{ fontWeight: 700, color: 'var(--nv)', marginBottom: '6px' }}>⚠️ Maintenance Notice</div>
            <div style={{ marginBottom: '6px' }}>Dear User,</div>
            <div style={{ marginBottom: '6px' }}>
              Due to scheduled maintenance, certain services,<b> including downloading PDF reports</b>, will be unavailable from <b>2 October, 6:00 PM</b> to <b>3 October, 10:00 AM</b>. All other application features will continue to work as usual.
            </div>
            <div style={{ marginBottom: '6px' }}>We apologise for the inconvenience and appreciate your understanding.</div>
            <div style={{ fontWeight: 600 }}>— SwiftCheq Team</div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Register Company Page (pre-login) ─────────────────────
// Collects just Company Name + Email. Submitting emails the user a set of
// login credentials; on their first login the backend detects the account
// is still self-signup-pending and routes them into Register (below) to
// finish the Company Info / GST Details / Agreement wizard.
const EMAIL_RE = /^([a-zA-Z0-9_.+-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
const PHONE_RE = /\(?([0-9]{3})\)?([ .-]?)([0-9]{3})\2([0-9]{4})/;

export function RegisterCompany({ onNavigate }) {
  const [companyName, setCompanyName] = useState('');
  const [clientName, setClientName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [companyNameError, setCompanyNameError] = useState('');
  const [clientNameError, setClientNameError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaSvg, setCaptchaSvg] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const { addToast } = useApp();

  async function loadCaptcha() {
    setCaptchaAnswer('');
    try {
      const res = await authApi.getCaptcha();
      setCaptchaId(res.captchaid || '');
      setCaptchaSvg(res.svg || '');
    } catch { /* leave captcha blank — submit will fail with a clear server-side error */ }
  }

  useEffect(() => { loadCaptcha(); }, []);

  async function handleSubmit() {
    const missingCompanyName = !companyName.trim();
    const missingClientName = !clientName.trim();
    const missingPhone = !phone.trim();
    const missingEmail = !email.trim();
    const invalidPhone = !missingPhone && !PHONE_RE.test(phone.trim());
    const invalidEmail = !missingEmail && !EMAIL_RE.test(email.trim());
    const missingCaptcha = !captchaAnswer.trim();

    setCompanyNameError(missingCompanyName ? 'Company name is required' : '');
    setClientNameError(missingClientName ? 'Client name is required' : '');
    setPhoneError(missingPhone ? 'Phone is required' : invalidPhone ? 'Invalid Number' : '');
    setEmailError(missingEmail ? 'Email address is required' : invalidEmail ? 'Invalid Email Id' : '');
    setCaptchaError(missingCaptcha ? 'Verification code is required' : '');

    if (missingCompanyName || missingClientName || missingPhone || missingEmail || invalidPhone || invalidEmail || missingCaptcha) {
      return;
    }

    setSubmitting(true);
    try {
      const res = await authApi.registerCompany(companyName.trim(), clientName.trim(), phone.trim(), email.trim(), captchaId, captchaAnswer.trim());
      // addToast(res.message || "We've sent a verification link to your email.", 'success');
      setSent(true);
    } catch (e) {
      addToast(e.message || 'Registration failed. Please try again.', 'error');
      loadCaptcha(); // captcha is single-use — refresh after every attempt, pass or fail
    } finally { setSubmitting(false); }
  }

  return (
    <>
      <AuthNavbar onNavigate={onNavigate} showRegister={false} />
      <div className="rc-page">
        <div className="rc-shell">
          {/* ── Left: marketing ── */}
          <div className="rc-left">
            <div className="rc-eyebrow">Get started free</div>
            <h1 className="rc-h1">Be <span className="rc-red">sure</span> about every hire — <span className="rc-it">the way enterprises are.</span></h1>
            <p className="rc-lead">SwiftCheq is a self-serve background verification platform that brings cFIRST's accredited checks, trusted by Fortune 500 teams, to India's growing businesses. Register in two minutes, load credit, and verify anyone you bring on.</p>

            <ul className="rc-ticks">
              <li><span className="rc-tick">✓</span><span><b>No contracts, no minimums.</b> Load a prepaid wallet and pay only for the checks you verify.</span></li>
              <li><span className="rc-tick">✓</span><span><b>Fully self-serve.</b> Order, track and download signed reports yourself — no sales calls to sit through.</span></li>
              <li><span className="rc-tick">✓</span><span><b>Candidates self-submit.</b> Share a secure link and they upload their own documents and consent, from any phone.</span></li>
              <li><span className="rc-tick">✓</span><span><b>Enterprise-grade compliance.</b> ISO 27001, ISO 27701, DPDP and GDPR aligned, with consent captured for every check.</span></li>
            </ul>

            <div className="rc-stats">
              <div className="rc-stat"><div className="n">2,000+</div><div className="t">clients trust cFIRST</div></div>
              <div className="rc-stat"><div className="n">5mn+</div><div className="t">checks a year</div></div>
              <div className="rc-stat"><div className="n">185+</div><div className="t">countries covered</div></div>
            </div>

            <div className="rc-steps">
              <div className="h">What happens next</div>
              <ol>
                <li><span>Register your company and verify your email</span></li>
                <li><span>Receive your <b>login credentials</b> by email</span></li>
                <li><span>Load your wallet by <b>UPI, Net Banking or Card</b></span></li>
                <li><span>Order your first check and download the report</span></li>
              </ol>
            </div>
          </div>

          {/* ── Right: form ── */}
          <div className="rc-right">
            <div className="rc-formcard">
              <div className="rc-cap">
                <h2>Register your company</h2>
                <p>Takes about two minutes. No card required.</p>
              </div>
              <div className="rc-body">
                {sent ? (
                  <div style={{ textAlign: 'center', padding: '10px 0' }}>
                    <div style={{ fontSize: '38px', marginBottom: '10px' }}>📧</div>
                    <div style={{ fontFamily: 'var(--rc-disp)', fontSize: '16px', fontWeight: 800, color: 'var(--rc-ink900)', marginBottom: '6px' }}>Check your inbox</div>
                    <div style={{ fontSize: '13.5px', color: 'var(--rc-muted)', marginBottom: '18px' }}>We've sent a verification link to <strong>{email}</strong>. Click the link to activate your account — we'll email your login credentials once it's verified.</div>
                    <button className="rc-submit" onClick={() => onNavigate('login')}>Go to Login →</button>
                  </div>
                ) : (
                  <div>
                    <div className="rc-field">
                      <label className="rc-label">Company / Organisation Name <span className="rc-req">*</span></label>
                      <input
                        className={`rc-input ${companyNameError ? 'rc-input-err' : ''}`} placeholder="Company / Organisation Name" value={companyName}
                        onChange={e => { setCompanyName(e.target.value); if (companyNameError) setCompanyNameError(''); }}
                      />
                      {companyNameError && <div className="rc-errtext">{companyNameError}</div>}
                    </div>
                    <div className="rc-field">
                      <label className="rc-label">Client Name <span className="rc-req">*</span></label>
                      <input
                        className={`rc-input ${clientNameError ? 'rc-input-err' : ''}`} placeholder="Full name" value={clientName}
                        onChange={e => { setClientName(e.target.value); if (clientNameError) setClientNameError(''); }}
                      />
                      {clientNameError && <div className="rc-errtext">{clientNameError}</div>}
                    </div>
                    <div className="rc-field">
                      <label className="rc-label">Phone <span className="rc-req">*</span></label>
                      <input
                        className={`rc-input ${phoneError ? 'rc-input-err' : ''}`} placeholder="+91 XXXXX XXXXX" value={phone}
                        onChange={e => { setPhone(e.target.value); if (phoneError) setPhoneError(''); }}
                      />
                      {phoneError && <div className="rc-errtext">{phoneError}</div>}
                    </div>
                    <div className="rc-field">
                      <label className="rc-label">Work Email Address <span className="rc-req">*</span></label>
                      <input
                        className={`rc-input ${emailError ? 'rc-input-err' : ''}`} type="email" placeholder="you@company.com" value={email}
                        onChange={e => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                        onKeyDown={e => { if (e.key === 'Enter' && !submitting) handleSubmit(); }}
                      />
                      {emailError && <div className="rc-errtext">{emailError}</div>}
                    </div>
                    <div className="rc-field">
                      <label className="rc-label">Verification Code <span className="rc-req">*</span></label>
                      <div className="rc-caprow">
                        <div className="rc-capbox" dangerouslySetInnerHTML={{ __html: captchaSvg }} />
                        <button type="button" className="rc-refresh" onClick={loadCaptcha} title="Refresh code" aria-label="Refresh code">↻</button>
                        <div style={{ flex: 1 }}>
                          <input
                            className={`rc-input ${captchaError ? 'rc-input-err' : ''}`} placeholder="4-digit code" value={captchaAnswer} maxLength={4} inputMode="numeric"
                            onChange={e => { setCaptchaAnswer(e.target.value.replace(/\D/g, '')); if (captchaError) setCaptchaError(''); }}
                            onKeyDown={e => { if (e.key === 'Enter' && !submitting) handleSubmit(); }}
                            style={{ letterSpacing: '4px', fontWeight: 700 }}
                          />
                          {captchaError && <div className="rc-errtext">{captchaError}</div>}
                        </div>
                      </div>
                    </div>
                    <button className="rc-submit" onClick={handleSubmit} disabled={submitting}>
                      {submitting ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Submitting…</> : 'Create my account →'}
                    </button>
                    <p className="rc-note">We'll email you a verification link — click it to activate your account and receive your login credentials.</p>
                    <div className="rc-secure">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M12 2 4 5v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V5z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      Your data is encrypted and handled under DPDP &amp; GDPR
                    </div>
                  </div>
                )}
                <div className="rc-loginhint">
                  Already have an account? <span onClick={() => onNavigate('login')}>Login</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer className="rc-footer">
          <div className="in">
            <span>© {new Date().getFullYear()} SwiftCheq — a cFIRST brand. Questions? <a href="mailto:hello@swiftcheq.in">hello@swiftcheq.in</a></span>
            <span className="mono">swiftcheq.in</span>
          </div>
        </footer>
      </div>
    </>
  );
}

// ── Change Password (Forceful) Page ──────────────────────
export function ChangePasswordForcefully({ onNavigate, username = '' }) {
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { addToast } = useApp();

  const allFilled = oldPwd && newPwd && confirmPwd;
  const matched = newPwd && confirmPwd && newPwd === confirmPwd;
  const mismatch = newPwd && confirmPwd && newPwd !== confirmPwd;
  const canSubmit = allFilled && matched && !submitting;

  async function handleChange() {
    if (!allFilled) { addToast('All fields are required', 'error'); return; }
    if (!matched) { addToast('New password and confirm password do not match', 'error'); return; }
    setSubmitting(true);
    try {
      const res = await authApi.changePasswordForcefully(oldPwd, confirmPwd);
      addToast(res.message || 'Password changed successfully!', 'success');
      onNavigate('login');
    } catch (e) {
      addToast(e.message || 'Password change failed', 'error');
    } finally { setSubmitting(false); }
  }

  return (
    <>
      <AuthNavbar onNavigate={onNavigate} showLogin={false} showRegister={false} />
      <div className="auth-wrap">
        <div className="auth-box">
          <div className="auth-logo">
            <img src={cFirstLogo} alt="cFIRST BGV" style={{ height: '36px', width: 'auto', margin: '0 auto 12px' }} />
            <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--nv)' }}>Change Password</div>
            <div style={{ fontSize: '13px', color: 'var(--g600)', marginTop: '3px' }}>Your password has expired. Please set a new password to continue.</div>
          </div>
          <div className="card" style={{ boxShadow: '0 8px 40px rgba(26,31,54,.13)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div className="fg">
                <label className="flbl">Username</label>
                <input className="inp" value={username} disabled style={{ background: 'var(--g50)', color: 'var(--g500)', cursor: 'not-allowed' }} />
              </div>
              <div className="fg">
                <label className="flbl">Old Password <span className="req">*</span></label>
                <input className="inp" type="password" placeholder="Enter current password" value={oldPwd} onChange={e => setOldPwd(e.target.value)} />
              </div>
              <div className="fg">
                <label className="flbl">New Password <span className="req">*</span></label>
                <input className="inp" type="password" placeholder="Enter new password" value={newPwd} onChange={e => setNewPwd(e.target.value)} />
              </div>
              <div className="fg">
                <label className="flbl">Confirm Password <span className="req">*</span></label>
                <input
                  className="inp" type="password" placeholder="Re-enter new password"
                  value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
                  style={mismatch ? { borderColor: 'var(--rd)'} : matched ? { borderColor: 'var(--gr)' } : {}}
                />
                {mismatch && <div style={{ fontSize: '11.5px', color: 'var(--rd)', marginTop: 2 }}>Passwords do not match!</div>}
                {matched && <div style={{ fontSize: '11.5px', color: 'var(--gr)', marginTop: 2 }}>Passwords matched</div>}
              </div>
              <button
                className="btn bpri wf blg" onClick={handleChange} disabled={!canSubmit}
                style={!canSubmit ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
              >
                {submitting ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Changing…</> : '🔒 Change Password'}
              </button>
              <button className="btn bghost wf" onClick={() => onNavigate('login')}>Cancel</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Forgot Password Page ──────────────────────────────────
export function ForgotPassword({ onNavigate }) {
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { addToast } = useApp();

  async function handleSubmit() {
    if (!username.trim()) {
      setUsernameError(true);
      return;
    }
    setUsernameError(false);
    setSubmitting(true);
    try {
      const res = await authApi.forgotPassword(username.trim());
      addToast(res.message || 'Check your Mailbox for login credentials.', 'success');
      onNavigate('login');
    } catch (e) {
      addToast(e.message || 'Request failed. Please try again.', 'error');
    } finally { setSubmitting(false); }
  }

  return (
    <>
      <AuthNavbar onNavigate={onNavigate} showLogin={false} showRegister={false} />
      <div className="auth-wrap">
        <div className="auth-box">
          <div className="auth-logo">
            <img src={cFirstLogo} alt="cFIRST BGV" style={{ height: '36px', width: 'auto', margin: '0 auto 12px' }} />
            <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--nv)' }}>Forgot Password</div>
            <div style={{ fontSize: '13px', color: 'var(--g600)', marginTop: '3px' }}>Enter your username and we'll send your login credentials to your registered email.</div>
          </div>
          <div className="card" style={{ boxShadow: '0 8px 40px rgba(26,31,54,.13)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div className="fg">
                <label className="flbl">UserName <span className="req">*</span></label>
                <input
                  className="inp"
                  placeholder="Enter your username"
                  value={username}
                  onChange={e => { setUsername(e.target.value); if (usernameError) setUsernameError(false); }}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                  style={usernameError ? { borderColor: 'var(--rd)' } : {}}
                />
                {usernameError && <div style={{ fontSize: '11.5px', color: 'var(--rd)', marginTop: 2 }}>Username is required</div>}
              </div>
              <button
                className="btn bpri wf blg"
                onClick={handleSubmit}
                disabled={submitting}
                style={submitting ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
              >
                {submitting ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Sending…</> : '📧 Submit'}
              </button>
              <button className="btn bghost wf" onClick={() => onNavigate('login')}>← Back to Login</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── ClientAuth OTP Verification Page ─────────────────────
export function ClientAuth({ onNavigate, clientotp, otpexpire, authToken, authUser }) {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [pageError, setPageError] = useState('');
  const [countdown, setCountdown] = useState(120);
  const [currentClientOtp, setCurrentClientOtp] = useState(clientotp);
  const [currentOtpExpire, setCurrentOtpExpire] = useState(otpexpire);
  const { addToast, login } = useApp();
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  function formatCountdown(s) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  }

  async function handleVerify() {
    if (otp.length !== 6) { setPageError('Please enter the 6-digit OTP.'); return; }
    setPageError('');
    setLoading(true);
    try {
      const res = await authApi.clientAuth(otp, currentOtpExpire, currentClientOtp);
      if (res.page === 'Dashboard') {
        login(authUser || {}, authToken, (authUser || {}).ClientId || (authUser || {}).clientid);
        onNavigate('dashboard');
        return;
      }
      setPageError(res.message || 'OTP verification failed.');
    } catch (e) {
      if (e.page === 'Login') {
        addToast(e.message || 'Session expired. Please login again.', 'error');
        onNavigate('login');
      } else {
        setPageError(e.message || 'OTP verification failed.');
      }
    } finally { setLoading(false); }
  }

  async function handleResend() {
    if (countdown > 0 || resending) return;
    setResending(true);
    setPageError('');
    try {
      const res = await authApi.resendClientOTP();
      const newOtp = res.clientOTP || res.clientotp || '';
      const newExpire = res.OTPExpire || res.otpexpire || '';
      setCurrentClientOtp(newOtp);
      setCurrentOtpExpire(newExpire);
      setOtp('');
      setCountdown(120);
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setCountdown(c => {
          if (c <= 1) { clearInterval(timerRef.current); return 0; }
          return c - 1;
        });
      }, 1000);
      addToast('OTP resent successfully.', 'success');
    } catch (e) {
      if (e.page === 'Login') {
        addToast(e.message || 'Session expired. Please login again.', 'error');
        onNavigate('login');
      } else {
        addToast(e.message || 'Failed to resend OTP. Please try again.', 'error');
      }
    } finally { setResending(false); }
  }

  return (
    <>
      <AuthNavbar onNavigate={onNavigate} showLogin={false} showRegister={false} />
      <div className="auth-wrap">
        <div className="auth-box">
          <div className="auth-logo">
            <img src={cFirstLogo} alt="cFIRST BGV" style={{ height: '36px', width: 'auto', margin: '0 auto 12px' }} />
            <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--nv)' }}>OTP Verification</div>
            <div style={{ fontSize: '13px', color: 'var(--g600)', marginTop: '3px' }}>Enter the 6-digit OTP sent to your email</div>
          </div>
          <div className="card" style={{ boxShadow: '0 8px 40px rgba(26,31,54,.13)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {pageError && (
                <div style={{ padding: '10px 14px', background: 'rgba(229,62,62,.08)', border: '1px solid rgba(229,62,62,.3)', borderRadius: '8px', fontSize: '13px', color: 'var(--rd)', fontWeight: 500 }}>
                  {pageError}
                </div>
              )}
              <div className="fg">
                <label className="flbl">Enter OTP <span className="req">*</span></label>
                <input
                  className="inp"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="• • • • • •"
                  value={otp}
                  onChange={e => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setPageError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleVerify()}
                  style={{ letterSpacing: '10px', fontSize: '22px', fontWeight: 700, textAlign: 'center' }}
                />
              </div>
              <button
                className="btn bpri wf blg"
                onClick={handleVerify}
                disabled={loading || otp.length !== 6}
                style={(loading || otp.length !== 6) ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
              >
                {loading ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Verifying…</> : 'Verify OTP →'}
              </button>
              <div style={{ textAlign: 'center', fontSize: '13px', color: 'var(--g500)' }}>
                {countdown > 0 ? (
                  <>Resend OTP in <strong style={{ color: 'var(--nv)' }}>{formatCountdown(countdown)}</strong></>
                ) : (
                  <span
                    onClick={handleResend}
                    style={{ color: resending ? 'var(--g400)' : 'var(--or-dk)', fontWeight: 600, cursor: resending ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    {resending ? <><span className="spinner" style={{ width: 11, height: 11, borderColor: 'var(--g300)', borderTopColor: 'var(--g500)' }} /> Resending…</> : 'Resend OTP'}
                  </span>
                )}
              </div>
              <button className="btn bghost wf" onClick={() => onNavigate('login')}>← Back to Login</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Verify Email Page ────────────────────────────────────
// Landed on from the verification link emailed by RegisterCompany (App.jsx turns the
// `?verify=<token>` query param into this page + a `token` param). Calling verifyEmail
// here is what actually creates the account and triggers the login-credentials email.
export function VerifyEmail({ onNavigate, token = '' }) {
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) { setStatus('error'); setMessage('Missing verification link.'); return; }
    (async () => {
      try {
        const res = await authApi.verifyEmail(token);
        setMessage(res.message || 'Your email has been verified.');
        setStatus('success');
      } catch (e) {
        setMessage(e.message || 'Verification failed.');
        setStatus('error');
      }
    })();
  }, [token]);

  return (
    <>
      <AuthNavbar onNavigate={onNavigate} showLogin={false} showRegister={false} />
      <div className="auth-wrap">
        <div className="auth-box">
          <div className="auth-logo">
            <img src={cFirstLogo} alt="cFIRST BGV" style={{ height: '36px', width: 'auto', margin: '0 auto 12px' }} />
            <div style={{ fontSize: '22px', fontWeight: 800, color: 'var(--nv)' }}>Email Verification</div>
          </div>
          <div className="card" style={{ boxShadow: '0 8px 40px rgba(26,31,54,.13)' }}>
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              {status === 'verifying' && (
                <>
                  <span className="spinner" style={{ width: 28, height: 28 }} />
                  <div style={{ fontSize: '13px', color: 'var(--g600)', marginTop: '14px' }}>Verifying your email…</div>
                </>
              )}
              {status === 'success' && (
                <>
                  <div style={{ fontSize: '38px', marginBottom: '10px' }}>✅</div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--nv)', marginBottom: '6px' }}>Verified!</div>
                  <div style={{ fontSize: '13px', color: 'var(--g600)', marginBottom: '18px' }}>{message}</div>
                  <button className="btn bpri wf blg" onClick={() => onNavigate('login')}>Go to Login →</button>
                </>
              )}
              {status === 'error' && (
                <>
                  <div style={{ fontSize: '38px', marginBottom: '10px' }}>⚠️</div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--nv)', marginBottom: '6px' }}>Verification failed</div>
                  <div style={{ fontSize: '13px', color: 'var(--g600)', marginBottom: '18px' }}>{message}</div>
                  <button className="btn bpri wf blg" onClick={() => onNavigate('register-company')}>Register again →</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
